<script setup>
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { computed } from 'vue';

const props = defineProps({
  channelType: {
    type: String,
    required: true,
  },
  medium: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  connectionStatus: {
    type: String,
    default: '',
  },
});
const getters = useStoreGetters();
const { t } = useI18n();
const globalConfig = computed(() => getters['globalConfig/get'].value);

const i18nMap = {
  'Channel::FacebookPage': 'MESSENGER',
  'Channel::WebWidget': 'WEB_WIDGET',
  'Channel::TwitterProfile': 'TWITTER_PROFILE',
  'Channel::TwilioSms': 'TWILIO_SMS',
  'Channel::Whatsapp': 'WHATSAPP',
  'Channel::Sms': 'SMS',
  'Channel::Email': 'EMAIL',
  'Channel::Telegram': 'TELEGRAM',
  'Channel::Line': 'LINE',
  'Channel::Api': 'API',
  'Channel::Instagram': 'INSTAGRAM',
  'Channel::Voice': 'VOICE',
};

const twilioChannelName = () => {
  if (props.medium === 'whatsapp') {
    return t(`INBOX_MGMT.CHANNELS.WHATSAPP`);
  }
  return t(`INBOX_MGMT.CHANNELS.TWILIO_SMS`);
};

const WHATSAPP_PROVIDER_I18N_MAP = {
  default: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.DEFAULT',
  whatsapp_cloud: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.WHATSAPP_CLOUD',
  baileys: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.BAILEYS',
  evolution: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.EVOLUTION',
  waha: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.WAHA',
  uazapi: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.UAZAPI',
  notificame: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.NOTIFICAME',
};

const readableChannelName = computed(() => {
  if (props.channelType === 'Channel::Api') {
    return globalConfig.value.apiChannelName || t('INBOX_MGMT.CHANNELS.API');
  }
  if (props.channelType === 'Channel::TwilioSms') {
    return twilioChannelName();
  }
  if (props.channelType === 'Channel::Whatsapp') {
    return t(`INBOX_MGMT.CHANNELS.WHATSAPP`);
  }
  return t(`INBOX_MGMT.CHANNELS.${i18nMap[props.channelType]}`);
});

const providerLabel = computed(() => {
  if (props.channelType !== 'Channel::Whatsapp') {
    return '';
  }

  const providerKey = WHATSAPP_PROVIDER_I18N_MAP[props.provider];
  if (!providerKey) {
    return '';
  }

  const label = t(providerKey);
  if (!label || label === providerKey) {
    return '';
  }

  return label;
});

const CONNECTED_STATUSES = new Set([
  'connected',
  'open',
  'working',
  'online',
  'ready',
]);
const CONNECTING_STATUSES = new Set([
  'connecting',
  'starting',
  'pending',
  'scan_qr_code',
  'scan-qr-code',
  'scan qr code',
]);

const normalizedConnectionStatus = computed(() =>
  (props.connectionStatus || '').toString().trim().toLowerCase()
);

const providerBadgeClasses = computed(() => {
  if (!providerLabel.value) {
    return '';
  }

  const baseClass =
    'inline-flex items-center rounded-full border px-1 py-0 text-[8px] font-semibold uppercase tracking-wide';
  const status = normalizedConnectionStatus.value;

  if (!status) {
    return `${baseClass} border-n-alpha-4 bg-n-alpha-2 text-n-slate-10`;
  }

  if (CONNECTED_STATUSES.has(status)) {
    return `${baseClass} border-green-200 bg-green-100 text-green-700`;
  }

  if (CONNECTING_STATUSES.has(status)) {
    return `${baseClass} border-yellow-200 bg-yellow-100 text-yellow-700`;
  }

  return `${baseClass} border-red-200 bg-red-100 text-red-700`;
});
</script>

<template>
  <span class="flex items-center gap-1 text-sm text-n-slate-11">
    <span>
      {{ readableChannelName }}
    </span>
    <span v-if="providerLabel" :class="providerBadgeClasses">
      {{ providerLabel }}
    </span>
  </span>
</template>
